import { useAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { productSelectionAtom } from '~/features/ProductSelection';
import { useSessionAdmin } from '~/shared/lib/auth';
import { TooltipAction } from '~/shared/ui/TooltipAction';
import { useTooltip } from '../model/hooks';

import { BookmarkPlus } from 'lucide-react';
import { ProductCategoriesDialog } from '~/features/ProductCategoriesModal';
import { useProductFilter } from '~/features/ProductFilter';
import { useProductSorting } from '~/features/ProductSorting';
import IconCancel from '~/shared/assets/icons/cancel.svg';
import DiscountIcon from '~/shared/assets/icons/discount.svg';
import SwitchVertical from '~/shared/assets/icons/switch-vertical.svg';

import type { ReorderContext } from '../model/types';
import { ProductSetDiscountDialog } from './ProductSetDiscountDialog';
import { ProductSetPositionDialog } from './ProductSetPositionDialog';

import CategoryIcon from '~/shared/assets/icons/layers.svg';
import { DeleteProductsAction } from './DeleteProductsAction';
import { ProductSelectionListsDialog } from './ProductSelectionListsDialog';
import { RemoveProductsFromSelectionListAction } from './RemoveProductsFromSelectionListAction';

type AdminProductSelectionActionsProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
};

export const AdminProductSelectionActions: React.FC<AdminProductSelectionActionsProps> = ({ open, setOpen }) => {
    const { isAdmin } = useSessionAdmin();
    const tooltip = useTooltip(isAdmin);

    const [sorting] = useProductSorting();
    const productFilter = useProductFilter();

    const isSorted = useMemo(() => sorting !== null && sorting !== undefined, [sorting]);
    const isFiltered = useMemo(() => productFilter[2], [productFilter[2]]);

    const [positionDialogCtx, setPositionDialogCtx] = useState<ReorderContext | null>(null);
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
    const [productSelection, setProductSelection] = useAtom(productSelectionAtom);
    const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
    const [selectionListsDialogOpen, setSelectionListsDialogOpen] = useState(false);

    const productsInCategory = useMemo(() => {
        if (!tooltip?.category) return [];

        return productSelection.filter(product =>
            product.inCategories?.some(cat => cat.categoryId === tooltip.category?.id),
        );
    }, [productSelection, tooltip?.category]);

    const productsInList = useMemo(() => {
        if (!tooltip?.list) return [];

        return productSelection.filter(product =>
            product.inSelectionLists?.some(cat => cat.selectionListId === tooltip.list?.item.id),
        );
    }, [productSelection, tooltip?.list]);

    const categoryInfo = useMemo(() => {
        if (tooltip?.type === 'category' && tooltip.category) {
            return { id: tooltip.category.id, name: tooltip.category.name };
        }
        return undefined;
    }, [tooltip]);

    const listInfo = useMemo(() => {
        if (tooltip?.type === 'list' && tooltip.list?.item) {
            return { id: tooltip.list.item.id, name: tooltip.list.item.title };
        }
        return undefined;
    }, [tooltip]);

    const scopedProducts = useMemo<ReadonlyArray<{ id: string }>>(() => {
        if (!positionDialogCtx) return [];
        const src = positionDialogCtx.type === 'category' ? productsInCategory : productsInList;
        return src.map(({ id }) => ({ id }));
    }, [positionDialogCtx, productsInCategory, productsInList]);

    const selectedProducts = useMemo<{ id: string }[]>(
        () => productSelection.map(({ id }) => ({ id })),
        [productSelection],
    );

    const canRemoveFromCurrentList = useMemo(() => {
        if (!listInfo) return false;
        if (selectedProducts.length === 0) return false;
        return productsInList.length === selectedProducts.length;
    }, [listInfo, productsInList.length, selectedProducts.length]);

    const handleCancelClick = useCallback(() => {
        setProductSelection([]);
        setOpen(false);
    }, [setOpen, setProductSelection]);

    return (
        <>
            {positionDialogCtx && (
                <ProductSetPositionDialog
                    open={!!positionDialogCtx}
                    onOpenChange={(next, clear) => {
                        if (!next && clear) setProductSelection([]);
                        if (!next) {
                            setPositionDialogCtx(null);
                            setOpen(false);
                        }
                    }}
                    products={scopedProducts}
                    context={positionDialogCtx}
                />
            )}

            {discountDialogOpen && (
                <ProductSetDiscountDialog
                    open={discountDialogOpen}
                    onOpenChange={(next, clear) => {
                        if (!next && clear) setProductSelection([]);
                        if (!next) {
                            setDiscountDialogOpen(false);
                            setOpen(false);
                        }
                    }}
                    products={selectedProducts}
                />
            )}

            {categoriesDialogOpen && (
                <ProductCategoriesDialog
                    open={categoriesDialogOpen}
                    onOpenChange={next => {
                        if (!next) {
                            setCategoriesDialogOpen(false);
                            setOpen(false);
                        }
                    }}
                    products={selectedProducts}
                />
            )}

            {selectionListsDialogOpen && (
                <ProductSelectionListsDialog
                    open={selectionListsDialogOpen}
                    onOpenChange={(next, clear) => {
                        if (!next && clear) setProductSelection([]);
                        if (!next) {
                            setSelectionListsDialogOpen(false);
                            setOpen(false);
                        }
                    }}
                    products={selectedProducts}
                />
            )}

            {selectedProducts.length > 0 && (
                <DeleteProductsAction
                    open={open}
                    index={open ? 0 : 7}
                    products={selectedProducts}
                    onOpenChange={(next, clear) => {
                        if (!next && clear) setProductSelection([]);
                        if (!next) {
                            setOpen(false);
                        }
                    }}
                />
            )}

            {canRemoveFromCurrentList && listInfo && (
                <RemoveProductsFromSelectionListAction
                    open={open}
                    index={open ? 1 : 6}
                    selectionListId={listInfo.id}
                    selectionListName={listInfo.name}
                    products={selectedProducts}
                    onOpenChange={(next, clear) => {
                        if (!next && clear) setProductSelection([]);
                        if (!next) setOpen(false);
                    }}
                />
            )}

            {selectedProducts.length > 0 && (
                <TooltipAction open={open} index={open ? 2 : 5}>
                    <button
                        type='button'
                        className='block h-full w-full p-2'
                        onClick={() => setSelectionListsDialogOpen(true)}
                        aria-label='Добавить в подборки'
                        title='Добавить в подборки'
                    >
                        <BookmarkPlus className='h-full w-full text-black' />
                    </button>
                </TooltipAction>
            )}

            {selectedProducts.length > 0 && (
                <TooltipAction open={open} index={open ? 3 : 4}>
                    <button
                        type='button'
                        className='block h-full w-full p-2'
                        onClick={() => setDiscountDialogOpen(true)}
                        aria-label='Установить скидку'
                        title='Установить скидку'
                    >
                        <DiscountIcon className='h-full w-full stroke-black' />
                    </button>
                </TooltipAction>
            )}

            {selectedProducts.length > 0 && (
                <TooltipAction open={open} index={open ? 4 : 3}>
                    <button
                        type='button'
                        className='block h-full w-full p-2'
                        onClick={() => setCategoriesDialogOpen(true)}
                        aria-label='Массовые категории'
                        title='Массовые категории'
                    >
                        <CategoryIcon className='h-full w-full stroke-black' />
                    </button>
                </TooltipAction>
            )}

            {categoryInfo && productsInCategory.length > 0 && !isFiltered && !isSorted && (
                <TooltipAction open={open} index={open ? 5 : 2}>
                    <button
                        type='button'
                        className='block h-full w-full p-2'
                        onClick={() =>
                            setPositionDialogCtx({
                                type: 'category',
                                id: categoryInfo.id,
                                name: categoryInfo.name,
                            })
                        }
                    >
                        <SwitchVertical className='h-full w-full stroke-black' />
                    </button>
                </TooltipAction>
            )}

            {listInfo && productsInList.length > 0 && !isFiltered && !isSorted && (
                <TooltipAction open={open} index={open ? 6 : 1}>
                    <button
                        type='button'
                        className='block h-full w-full p-2'
                        onClick={() =>
                            setPositionDialogCtx({
                                type: 'list',
                                id: listInfo.id,
                                name: listInfo.name,
                            })
                        }
                    >
                        <SwitchVertical className='h-full w-full stroke-black' />
                    </button>
                </TooltipAction>
            )}

            <TooltipAction open={open} index={open ? 7 : 0}>
                <button type='button' className={'block h-full w-full p-2'} onClick={handleCancelClick}>
                    <IconCancel className={'h-full w-full stroke-black'} />
                </button>
            </TooltipAction>
        </>
    );
};
